import React, { useState } from 'react';
import { DB } from '../data/database';
import { getDestinationCountries } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import StatCard from './ui/StatCard';
import { UploadIcon, InventoryIcon, StockIcon, CheckIcon } from './Icons';
import InspectionWizard from './InspectionWizard';

const cutLabel = (ct) => ({ cbu: 'CBU', half_cut: 'Half Cut', ckd: 'CKD' }[ct] || null);
const CUT_BADGE_STYLE = {
  cbu:      { background: '#dcfce7', color: '#166534' },
  half_cut: { background: '#fef3c7', color: '#92400e' },
  ckd:      { background: '#fee2e2', color: '#991b1b' },
};

function YardDashboard({ user, currency, lang }) {
  const { t } = useLanguage();
  const [showWizard, setShowWizard] = useState(false);
  const [, forceUpdate] = useState();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [workerByCountry, setWorkerByCountry] = useState({});

  const allUnits = [
    ...(DB.get('vehicles') || []),
    ...(DB.get('spareParts') || []),
    ...(DB.get('engines') || []),
  ];
  const isManager = user.role === 'yard_manager';
  const myUnits = isManager
    ? allUnits.filter(u => u.destination_yard === user.yard)
    : allUnits.filter(u => u.created_by === user.id);
  const myTasks = (DB.get('tasks') || []).filter(t => t.assigned_to === user.id);

  const availableCount = myUnits.filter(u => u.status === 'available' || u.status === 'japan_yard').length;
  const pendingTasks = myTasks.filter(t => t.status !== 'completed').length;
  const completedTasks = myTasks.filter(t => t.status === 'completed').length;

  const awaitingAssignment = isManager ? myUnits.filter(u => !u.destination_country) : [];
  const workList = isManager ? myUnits.filter(u => u.destination_country) : [];
  const grouped = {};
  workList.forEach(u => {
    (grouped[u.destination_country] = grouped[u.destination_country] || []).push(u);
  });
  const countryMeta = (id) => getDestinationCountries().find(c => c.id === id) || { name: id, flag: '🌍' };

  const allUsers = DB.get('users') || [];
  const operators = isManager
    ? allUsers.filter(u => u.role === 'yard_operator' && u.yard === user.yard)
    : [];

  const toggleId = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleGroup = (unitIds) => setSelectedIds(prev => {
    const next = new Set(prev);
    const allSelected = unitIds.every(id => next.has(id));
    unitIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
    return next;
  });

  const handleAssignGroup = async (countryId, groupUnits) => {
    const workerId = workerByCountry[countryId];
    if (!workerId) return;
    const groupSelected = groupUnits.filter(u => selectedIds.has(u.id));
    if (groupSelected.length === 0) return;
    const worker = operators.find(u => u.id === workerId);
    const meta = countryMeta(countryId);
    const tasks = DB.get('tasks') || [];
    const task = {
      id: DB.genId('TSK'),
      title: `Move ${groupSelected.length} unit${groupSelected.length !== 1 ? 's' : ''} — ${meta.name}`,
      description: `Assigned by ${user.name}. Units: ${groupSelected.map(u => u.sku).join(', ')}`,
      assigned_to: workerId,
      assigned_by: user.id,
      entity_type: 'vehicle',
      entity_ids: groupSelected.map(u => u.id),
      due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      priority: 'normal',
      status: 'pending',
      completed_at: null,
      total_items: groupSelected.length,
      completed_items: 0,
      attachments: [],
      notes: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const res = await DB.setAsync('tasks', [...tasks, task]);
    if (res?.error) { alert('Failed to assign: ' + res.error); return; }
    setSelectedIds(prev => {
      const next = new Set(prev);
      groupSelected.forEach(u => next.delete(u.id));
      return next;
    });
    setWorkerByCountry(prev => ({ ...prev, [countryId]: '' }));
    forceUpdate({});
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('dash_good_morning');
    if (h < 17) return t('dash_good_afternoon');
    return t('dash_good_evening');
  })();

  const unitName = (u) => u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unit';

  return (
    <div className="dashboard-page">
      <div className="welcome-banner" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>{greeting}, {user.name}</h2>
          <p style={{ opacity: 0.7, margin: '4px 0 0' }}>
            {isManager ? 'Yard Manager' : 'Yard Operator'}
            {user.yard && <span> — {user.yard}</span>}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowWizard(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', fontSize: 14, fontWeight: 600 }}>
          + New Inspection
        </button>
      </div>

      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard
          icon={isManager ? InventoryIcon : UploadIcon}
          value={myUnits.length}
          label={isManager ? 'Yard Units' : 'My Uploads'}
          color="blue"
        />
        <StatCard
          icon={StockIcon}
          value={pendingTasks}
          label="Pending Tasks"
          color="orange"
        />
        <StatCard
          icon={CheckIcon}
          value={completedTasks}
          label="Completed"
          color="green"
        />
        <StatCard
          icon={InventoryIcon}
          value={availableCount}
          label="Available"
          color="purple"
        />
      </div>

      {isManager && awaitingAssignment.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ marginBottom: 4 }}>Inspected — Awaiting Head Office</h3>
          <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>Done inspections, waiting for destination assignment.</p>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}>📋</span>
              <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text1, #e6edf3)' }}>Awaiting Assignment</span>
              <span style={{ fontSize: 12, color: 'var(--text3, #8b949e)', marginLeft: 4 }}>{awaitingAssignment.length} unit{awaitingAssignment.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {awaitingAssignment.map(u => (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 8,
                  background: 'var(--bg3)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 500, color: 'var(--text1, #e6edf3)' }}>{unitName(u)}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>{u.sku}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {u.created_by_name && (
                      <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>by {u.created_by_name}</span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(88,166,255,0.15)', color: 'var(--blue, #58a6ff)' }}>
                      Inspected
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isManager && workList.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ marginBottom: 4 }}>Work List — from Head Office</h3>
          <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>Assigned units, grouped by destination. Distribute to your team.</p>
          {Object.entries(grouped).map(([countryId, units]) => {
            const meta = countryMeta(countryId);
            const groupIds = units.map(u => u.id);
            const groupSelectedCount = groupIds.filter(id => selectedIds.has(id)).length;
            const allGroupSelected = groupSelectedCount === groupIds.length;
            return (
              <div key={countryId} className="card" style={{ padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input type="checkbox" checked={allGroupSelected} onChange={() => toggleGroup(groupIds)}
                    style={{ accentColor: 'var(--blue, #58a6ff)', width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 20 }}>{meta.flag}</span>
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text1, #e6edf3)' }}>{meta.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3, #8b949e)', marginLeft: 4 }}>{units.length} unit{units.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {units.map(u => (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 8,
                      background: selectedIds.has(u.id) ? 'rgba(88,166,255,0.08)' : 'var(--bg3)',
                      border: selectedIds.has(u.id) ? '1px solid rgba(88,166,255,0.25)' : '1px solid transparent',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleId(u.id)}
                          style={{ accentColor: 'var(--blue, #58a6ff)', width: 15, height: 15, cursor: 'pointer' }} />
                        <span style={{ fontWeight: 500, color: 'var(--text1, #e6edf3)' }}>{unitName(u)}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>{u.sku}</span>
                      </div>
                      {u.cut_type ? (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          ...(CUT_BADGE_STYLE[u.cut_type] || { background: '#f1f5f9', color: '#475569' }),
                        }}>
                          {cutLabel(u.cut_type)}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#94a3b8' }}>
                          Cut type pending
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {groupSelectedCount > 0 && operators.length > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginTop: 12,
                    padding: '10px 12px', borderRadius: 8, background: 'rgba(88,166,255,0.06)',
                    border: '1px solid rgba(88,166,255,0.2)',
                  }}>
                    <select
                      value={workerByCountry[countryId] || ''}
                      onChange={e => setWorkerByCountry(prev => ({ ...prev, [countryId]: e.target.value }))}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13 }}>
                      <option value="">Select operator…</option>
                      {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                    </select>
                    <button
                      className="btn btn-primary"
                      disabled={!workerByCountry[countryId]}
                      onClick={() => handleAssignGroup(countryId, units)}
                      style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}>
                      Assign {groupSelectedCount} unit{groupSelectedCount !== 1 ? 's' : ''}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isManager && (() => {
        const vehicleTasks = myTasks.filter(t =>
          t.entity_type === 'vehicle' && t.status !== 'completed' && t.status !== 'cancelled'
          && t.entity_ids && t.entity_ids.length > 0
        );
        const unitMap = new Map(allUnits.map(u => [u.id, u]));
        const resolved = vehicleTasks.map(t => ({
          task: t,
          units: t.entity_ids.map(id => unitMap.get(id)).filter(Boolean),
        })).filter(r => r.units.length > 0);
        if (resolved.length === 0) return null;
        return (
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ marginBottom: 4 }}>Assigned Units</h3>
            <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>Units assigned to you by your manager.</p>
            {resolved.map(({ task, units }) => (
              <div key={task.id} className="card" style={{ padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>📦</span>
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text1, #e6edf3)' }}>{task.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3, #8b949e)', marginLeft: 4 }}>{units.length} unit{units.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {units.map(u => (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 8,
                      background: 'var(--bg3)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 500, color: 'var(--text1, #e6edf3)' }}>{unitName(u)}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>{u.sku}</span>
                      </div>
                      {u.cut_type ? (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          ...(CUT_BADGE_STYLE[u.cut_type] || { background: '#f1f5f9', color: '#475569' }),
                        }}>
                          {cutLabel(u.cut_type)}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#94a3b8' }}>
                          Cut type pending
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {myTasks.length > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 16 }}>My Tasks</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myTasks.slice(0, 10).map(task => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 8,
                background: 'var(--bg3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: task.status === 'completed' ? '#22c55e' : task.status === 'in_progress' ? '#f59e0b' : '#94a3b8',
                  }} />
                  <span style={{ fontWeight: 500, color: 'var(--text1, #e6edf3)' }}>{task.title || task.description || 'Untitled task'}</span>
                </div>
                <span style={{
                  fontSize: 12, padding: '2px 8px', borderRadius: 4,
                  background: task.status === 'completed' ? '#dcfce7' : task.status === 'in_progress' ? '#fef3c7' : '#f1f5f9',
                  color: task.status === 'completed' ? '#166534' : task.status === 'in_progress' ? '#92400e' : '#475569',
                }}>
                  {task.status === 'completed' ? 'Done' : task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
          {myTasks.length > 10 && (
            <p style={{ marginTop: 12, fontSize: 13, opacity: 0.6 }}>
              +{myTasks.length - 10} more tasks
            </p>
          )}
        </div>
      )}

      {myTasks.length === 0 && (
        <div className="card" style={{ padding: 20, textAlign: 'center', opacity: 0.6 }}>
          <StockIcon style={{ width: 32, height: 32, marginBottom: 8 }} />
          <p>No tasks assigned yet</p>
        </div>
      )}

      {showWizard && (
        <InspectionWizard user={user} onClose={() => setShowWizard(false)} onComplete={() => forceUpdate({})} />
      )}
    </div>
  );
}

export default YardDashboard;
